// カックロ**固有**の設定（localStorage 永続）。
//
// ポータル横断の設定（音量・ミュート・BGM・言語）は
// [platform/net/settings.ts](@/platform/net/settings) が持つ。ここにあるのは
// 「このゲームでしか意味を持たない」項目だけ。所属の決め方は CLAUDE.md の
// 「設定は所属で2つに分かれる」を参照。
//
// キーは `kakuro.*`（＝`game_key` と同じ文字列）。データ削除が接頭辞で掃くので、
// ここに増やしたぶんは自動で消える対象になる。
import type { GameSettingsSlice } from '@/platform/net/settings';
import { GAME_KEY } from '../gameKey';

const K_HIGHLIGHT = `${GAME_KEY}.highlightOn`;
const K_COMBO = `${GAME_KEY}.comboHintOn`;

export const GAME_SETTINGS_DEFAULTS = {
  /** 選んだマスが属する**横と縦の並び**をうっすら示す。 */
  highlightOn: true,
  /** その並びに入りうる**数の組**を右パネルに出す。 */
  comboHintOn: true,
} as const;

/**
 * 並びの強調（既定 ON）。
 *
 * 🔴 **既定を ON にするのは親切のためではない**。カックロは
 *   「そのマスがどの並びに属するか」＝**どの2つの手がかりに縛られているか**が分からないと
 *   1手も打てない。⚠️ ところが盤にマスを置くと**自分の指で並びの端が隠れる**うえ、
 *   手がかりは並びの**反対の端**にあるので目で追う距離が長い。
 *   ⇒ 見誤ったまま置くとその場でミス＝ ❤️ が1つ減る（仕様6・7）のに、
 *   本人は「確かめたつもり」なので**原因が分からない**。
 *   消せるようにはするが、**既定では出す**。💡 ナンプレの `highlightOn` と同じ判断。
 */
export function isHighlightOn(): boolean {
  const v = localStorage.getItem(K_HIGHLIGHT);
  return v === null ? GAME_SETTINGS_DEFAULTS.highlightOn : v === '1';
}

export function setHighlightOn(on: boolean): void {
  localStorage.setItem(K_HIGHLIGHT, on ? '1' : '0');
}

/**
 * 組の表示（既定 ON・2026-09-08 ユーザー決定）。
 *
 * 🔴 **カックロは「組を覚えているか」を問うゲームではない**。紙で遊ぶときは
 *   「長さ×和 → 入る数の組」の表を手元に置くのが普通で、
 *   ⚠️ 表を持たない人だけが不利になるのは**遊びの差ではなく持ち物の差**。
 *   ⇒ 画面が表の役をする。
 * ⚠️ 前半は自明（長さ2・和3 は {1,2} しかない）なので効かないが、
 *   **Lv7 以降は無いと暗算前提**になる（いちばん多い 長さ4・和20 と 長さ5・和25 は**12通り**）。
 * 💡 実装は [game/combos.ts](../game/combos.ts) の前計算表を引くだけなので安い。
 */
export function isComboHintOn(): boolean {
  const v = localStorage.getItem(K_COMBO);
  return v === null ? GAME_SETTINGS_DEFAULTS.comboHintOn : v === '1';
}

export function setComboHintOn(on: boolean): void {
  localStorage.setItem(K_COMBO, on ? '1' : '0');
}

/** このゲームの設定を既定値へ戻す。 */
export function resetGameSettings(): void {
  setHighlightOn(GAME_SETTINGS_DEFAULTS.highlightOn);
  setComboHintOn(GAME_SETTINGS_DEFAULTS.comboHintOn);
}

/**
 * サーバー同期用のスライス。`registry.ts` に載せると
 * `/api/settings` の `games.<gameKey>` として自動で往復するようになる。
 */
export const kakuroSettingsSlice: GameSettingsSlice = {
  gameKey: GAME_KEY,
  read: () => ({ highlightOn: isHighlightOn(), comboHintOn: isComboHintOn() }),
  apply: (s) => {
    if (typeof s.highlightOn === 'boolean') setHighlightOn(s.highlightOn);
    if (typeof s.comboHintOn === 'boolean') setComboHintOn(s.comboHintOn);
  },
  reset: resetGameSettings,
};
